import React, { useState, useEffect } from 'react';
import { Edit2, Save, Image as ImageIcon, Type, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import api from '../utils/api';

const CMSTab = () => {
  const [seccionActual, setSeccionActual] = useState('home');
  const [configuracion, setConfiguracion] = useState(null);
  const [editando, setEditando] = useState(false);
  const [cambios, setCambios] = useState({});
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const secciones = [
    { id: 'home', nombre: '🏠 Inicio' },
    { id: 'blog', nombre: '📝 Blog' },
    { id: 'estudios', nombre: '🔬 Estudios' },
    { id: 'contacto', nombre: '📞 Contacto' },
    { id: 'footer', nombre: '📄 Footer' }
  ];

  useEffect(() => {
    cargarConfiguracion();
  }, [seccionActual]);

  const cargarConfiguracion = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/configuracion/seccion/${seccionActual}`);
      setConfiguracion(response.data);
      setCambios(response.data);
    } catch (error) {
      console.error('Error loading config:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar la configuración',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGuardar = async () => {
    try {
      setLoading(true);
      await api.put(`/configuracion/seccion/${seccionActual}`, cambios.textos);
      
      toast({
        title: '✅ Guardado exitoso',
        description: 'Los cambios han sido guardados correctamente',
      });
      
      setEditando(false);
      cargarConfiguracion();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron guardar los cambios',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCambioTexto = (campo, valor) => {
    setCambios(prev => ({
      ...prev,
      textos: {
        ...prev.textos,
        [campo]: valor
      }
    }));
  };

  const handleSubirImagen = async (campo, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('campo', campo);
    formData.append('imagen', file);

    try {
      setLoading(true);
      const response = await api.post(
        `/configuracion/seccion/${seccionActual}/imagen`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      setCambios(prev => ({
        ...prev,
        imagenes: {
          ...prev.imagenes,
          [campo]: response.data.url
        }
      }));

      toast({
        title: '✅ Imagen subida',
        description: 'La imagen ha sido actualizada',
      });

      cargarConfiguracion();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo subir la imagen',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!configuracion) {
    return <div className="text-center py-8">Cargando...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-[#1e3a8a]">🎨 Editor de Contenido (CMS)</CardTitle>
          {editando ? (
            <div className="flex gap-2">
              <Button
                onClick={handleGuardar}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
              >
                <Save className="h-4 w-4 mr-2" />
                Guardar cambios
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setEditando(false);
                  setCambios(configuracion);
                }}
              >
                <X className="h-4 w-4 mr-2" />
                Cancelar
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => setEditando(true)}
              className="bg-[#1e40af] hover:bg-[#1e3a8a]"
            >
              <Edit2 className="h-4 w-4 mr-2" />
              Editar contenido
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Selector de sección */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-3">Sección a editar:</label>
          <div className="flex gap-2 flex-wrap">
            {secciones.map((seccion) => (
              <Button
                key={seccion.id}
                variant={seccionActual === seccion.id ? 'default' : 'outline'}
                onClick={() => setSeccionActual(seccion.id)}
                className={seccionActual === seccion.id ? 'bg-[#1e40af]' : ''}
              >
                {seccion.nombre}
              </Button>
            ))}
          </div>
        </div>

        {/* Textos */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Type className="h-5 w-5" />
            Textos
          </h3>
          <div className="space-y-4">
            {Object.entries(cambios.textos || {}).map(([campo, valor]) => (
              <div key={campo}>
                <label className="block text-sm font-medium mb-2 capitalize">
                  {campo.replace(/_/g, ' ')}
                </label>
                {editando ? (
                  valor && valor.length > 100 ? (
                    <textarea
                      value={valor}
                      onChange={(e) => handleCambioTexto(campo, e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md min-h-[100px]"
                      rows="4"
                    />
                  ) : (
                    <Input
                      value={valor}
                      onChange={(e) => handleCambioTexto(campo, e.target.value)}
                    />
                  )
                ) : (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                    {valor}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Imágenes */}
        {cambios.imagenes && Object.keys(cambios.imagenes).length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <ImageIcon className="h-5 w-5" />
              Imágenes
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {Object.entries(cambios.imagenes || {}).map(([campo, url]) => (
                <Card key={campo} className="border-2">
                  <CardContent className="p-4">
                    <label className="block text-sm font-medium mb-2 capitalize">
                      {campo.replace(/_/g, ' ')}
                    </label>
                    {url && (
                      <div className="mb-3">
                        <img
                          src={url}
                          alt={campo}
                          className="w-full h-40 object-cover rounded-md border"
                        />
                      </div>
                    )}
                    {editando && (
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleSubirImagen(campo, e.target.files[0])}
                          className="w-full text-sm"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Formatos: JPG, PNG, WebP
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {editando && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              💡 <strong>Tip:</strong> Los cambios se verán reflejados inmediatamente en la página
              después de guardar. No olvides hacer clic en "Guardar cambios".
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CMSTab;
